<template>
  <div class="main-content">
    <div class="card p-2">
      <div class="mb-2 bread">
        WFR <span class="sep2">›</span>
        <span @click="$router.push('/Flow/templates')" class="bread-link"
          >Modelos de Flow</span
        >
        <span class="sep2">›</span>
        {{ isEdit ? "Editar modelo" : "Novo modelo" }}
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="m-0 page-title">
          {{ isEdit ? "Editar modelo de flow" : "Novo modelo de flow" }}
        </h4>
      </div>

      <!-- Informações gerais -->
      <div class="section-card">
        <div class="section-title">Informações do modelo</div>

        <div class="row">
          <div class="col-md-6">
            <label class="lbl">Título <span class="req">*</span></label>
            <input
              v-model="form.title"
              class="form-control"
              placeholder="Ex: Aprovação de Compras"
            />
          </div>
          <div class="col-md-6">
            <label class="lbl">Tag</label>
            <input
              v-model="form.tag"
              class="form-control"
              placeholder="Ex: APROVACAO_COMPRAS"
            />
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-12">
            <label class="lbl">Descrição</label>
            <textarea
              v-model="form.description"
              class="form-control text-area"
              rows="3"
              placeholder="Descreva quando este modelo deve ser usado..."
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Etapas -->
      <div class="section-card mt-3">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <div class="section-title m-0">Etapas</div>
            <div class="help-muted">
              Defina as etapas que compõem este modelo.
            </div>
          </div>
          <button
            class="btn btn-outline-primary btn-sm"
            @click="openAddStepModal"
          >
            <i class="fa fa-plus"></i> Adicionar etapa
          </button>
        </div>

        <div class="steps-shell mt-3">
          <div class="steps-line"></div>
          <div class="steps">
            <div
              v-for="(s, idx) in steps"
              :key="s.uid"
              class="step"
              :class="{ active: idx === selectedStepIndex }"
              @click="selectedStepIndex = idx"
            >
              <div class="step-pill">
                <div class="step-num" :class="numClass(idx)">{{ idx + 1 }}</div>
                <div class="step-info">
                  <div class="step-name">{{ s.name || "Sem nome" }}</div>
                  <div class="step-dept">{{ deptName(s.department_id) }}</div>
                  <div v-if="s.form_template_id" class="step-form-badge">
                    <i class="fa fa-list-alt"></i>
                    Formulário{{ s.form_required ? " obrigatório" : "" }}
                  </div>
                </div>
                <button
                  class="step-form-btn"
                  title="Formulário da etapa"
                  @click.stop="openStepFormModal(idx)"
                >
                  <i class="fa fa-list-alt"></i>
                </button>
                <button
                  v-if="idx !== 0"
                  class="step-x"
                  title="Remover etapa"
                  @click.stop="removeStep(idx)"
                >
                  ×
                </button>
              </div>

              <div class="step-actions">
                <button
                  class="mini-btn"
                  title="Mover para esquerda"
                  :disabled="idx === 0 || idx === 1"
                  @click.stop="moveStep(idx, -1)"
                >
                  <i class="fa fa-arrow-left"></i>
                </button>
                <button
                  class="mini-btn"
                  title="Mover para direita"
                  :disabled="idx === 0 || idx === steps.length - 1"
                  @click.stop="moveStep(idx, 1)"
                >
                  <i class="fa fa-arrow-right"></i>
                </button>
              </div>

              <div v-if="idx < steps.length - 1" class="arrow">→</div>
            </div>

            <button class="new-step" @click="openAddStepModal">
              <i class="fa fa-plus"></i> Nova etapa
            </button>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger mt-2 p-2">{{ error }}</div>

        <div
          class="d-flex justify-content-end align-items-center mt-3"
          style="gap: 10px"
        >
          <button class="btn btn-outline-secondary" @click="cancel">
            Voltar
          </button>
          <button class="btn btn-primary" :disabled="saving" @click="save">
            {{
              saving
                ? "Salvando..."
                : isEdit
                ? "Salvar alterações"
                : "Criar modelo"
            }}
          </button>
        </div>
      </div>

      <!-- Modal: adicionar etapa -->
      <div
        v-if="showStepModal"
        class="modal-mask"
        @click.self="closeAddStepModal"
      >
        <div class="modal-card">
          <div class="modal-head">
            <div class="modal-title">Adicionar nova etapa</div>
            <button class="modal-x" @click="closeAddStepModal">×</button>
          </div>
          <div class="modal-body">
            <label class="lbl">Nome da etapa <span class="req">*</span></label>
            <input
              v-model="newStep.name"
              class="form-control"
              placeholder="Ex: Revisão Financeira"
              @keyup.enter="confirmAddStep"
            />

            <div class="row mt-2">
              <div class="col-md-12">
                <label class="lbl">Área <span class="req">*</span></label>
                <select v-model="newStep.department_id" class="form-control">
                  <option value="" disabled>Selecionar área</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">
                    {{ d.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row mt-3">
              <div
                class="col-md-12 d-flex align-items-center"
                style="gap: 10px"
              >
                <input
                  id="reqApproval"
                  type="checkbox"
                  v-model="newStep.requires_approval"
                />
                <label for="reqApproval" class="m-0 lbl" style="cursor: pointer"
                  >Precisa de aprovação?</label
                >
              </div>
            </div>

            <div v-if="newStep.requires_approval" class="row mt-2">
              <div class="col-md-12">
                <label class="lbl">Usuário aprovador</label>
                <select v-model="newStep.approver_user_id" class="form-control">
                  <option value="">Selecionar usuário</option>
                  <option v-for="u in users" :key="u.id" :value="u.id">
                    {{ u.name }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="stepError" class="alert alert-danger mt-2 p-2">
              {{ stepError }}
            </div>
          </div>
          <div class="modal-foot">
            <button
              class="btn btn-outline-secondary"
              @click="closeAddStepModal"
            >
              Cancelar
            </button>
            <button class="btn btn-primary" @click="confirmAddStep">
              Adicionar
            </button>
          </div>
        </div>
      </div>

      <!-- Modal: formulário da etapa -->
      <div
        v-if="stepFormModal.open"
        class="modal-mask"
        @click.self="closeStepFormModal"
      >
        <div class="modal-card modal-card-lg">
          <div class="modal-head">
            <div class="modal-title">
              Formulário da etapa —
              {{ stepFormModal.stepIdx !== null ? steps[stepFormModal.stepIdx].name : "" }}
            </div>
            <button class="modal-x" @click="closeStepFormModal">×</button>
          </div>
          <div class="modal-body">
            <div class="help-muted mb-3">
              Esse formulário fica salvo no modelo: toda solicitação aberta a
              partir dele já vem com ele pré-preenchido (dá pra ajustar na
              hora de abrir a solicitação, se precisar).
            </div>

            <div class="d-flex align-items-center mb-3" style="gap: 10px">
              <input
                id="tplFormRequired"
                type="checkbox"
                v-model="stepFormModal.form_required"
              />
              <label for="tplFormRequired" class="m-0 lbl" style="cursor: pointer">
                Responder este formulário é obrigatório para concluir a etapa
              </label>
            </div>

            <div class="mode-tabs mb-3">
              <button
                class="mode-tab"
                :class="{ active: stepFormModal.mode === 'existing' }"
                @click="stepFormModal.mode = 'existing'"
              >
                Usar modelo existente
              </button>
              <button
                class="mode-tab"
                :class="{ active: stepFormModal.mode === 'scratch' }"
                @click="stepFormModal.mode = 'scratch'"
              >
                Criar modelo novo
              </button>
            </div>

            <div v-if="stepFormModal.mode === 'existing'">
              <label class="lbl">Modelo de formulário</label>
              <select
                v-model="stepFormModal.form_template_id"
                class="form-control"
                @change="applyFormTemplatePreview"
              >
                <option value="">Selecionar modelo...</option>
                <option v-for="tpl in formTemplates" :key="tpl.id" :value="tpl.id">
                  {{ tpl.title }}
                </option>
              </select>

              <div v-if="stepFormModal.questions.length" class="form-preview mt-3">
                <div class="form-preview-title">Perguntas deste modelo:</div>
                <div
                  v-for="q in stepFormModal.questions"
                  :key="q.uid || q.id"
                  class="form-preview-item"
                >
                  <i class="fa fa-circle-o"></i> {{ q.label }}
                  <span v-if="q.required" class="req">*</span>
                </div>
              </div>
            </div>

            <div v-else>
              <label class="lbl">Título do novo modelo <span class="req">*</span></label>
              <input
                v-model="stepFormModal.newTitle"
                class="form-control mb-3"
                placeholder="Ex: Checklist de aprovação financeira"
              />

              <question-builder v-model="stepFormModal.questions" />
            </div>

            <div v-if="stepFormError" class="alert alert-danger mt-2 p-2">
              {{ stepFormError }}
            </div>
          </div>
          <div class="modal-foot">
            <button
              v-if="stepFormModal.stepIdx !== null && steps[stepFormModal.stepIdx].form_template_id"
              class="btn btn-outline-danger mr-auto"
              @click="removeStepForm"
            >
              Remover formulário
            </button>
            <button class="btn btn-outline-secondary" @click="closeStepFormModal">
              Cancelar
            </button>
            <button
              class="btn btn-primary"
              :disabled="savingStepForm"
              @click="saveStepFormModal"
            >
              {{ savingStepForm ? "Salvando..." : "Salvar" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuestionBuilder from "../FormTemplates/QuestionBuilder.vue";

export default {
  components: { QuestionBuilder },

  data() {
    return {
      isEdit: false,
      saving: false,
      error: "",
      stepError: "",
      showStepModal: false,
      selectedStepIndex: 0,

      departments: [],
      users: [],
      formTemplates: [],

      stepFormModal: {
        open: false,
        stepIdx: null,
        form_required: false,
        mode: "existing",
        form_template_id: "",
        newTitle: "",
        questions: [],
      },
      stepFormError: "",
      savingStepForm: false,

      form: {
        title: "",
        tag: "",
        description: "",
      },

      steps: [
        {
          uid: "s1",
          name: "Abertura",
          department_id: "",
          requires_approval: false,
          approver_user_id: "",
          form_template_id: "",
          form_required: false,
        },
      ],

      newStep: {
        name: "",
        department_id: "",
        requires_approval: false,
        approver_user_id: "",
      },
    };
  },

  async mounted() {
    await Promise.all([this.loadMeta(), this.loadFormTemplates()]);
    if (this.$route.params.id) {
      this.isEdit = true;
      await this.loadTemplate(this.$route.params.id);
    }
  },

  methods: {
    async loadMeta() {
      try {
        const apiBase = this.$store.state.api;
        const [depsRes, usersRes] = await Promise.all([
          this.$http.get(apiBase + "departments?paginated=false"),
          this.$http.get(apiBase + "users?paginated=false"),
        ]);
        this.departments = depsRes.data.data || [];
        this.users = usersRes.data.data || [];
      } catch (e) {
        console.error(e);
        this.error = "Não consegui carregar departamentos/usuários.";
      }
    },

    async loadFormTemplates() {
      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(apiBase + "form-templates", {
          params: { paginated: false },
        });
        const raw = res.data;
        this.formTemplates = Array.isArray(raw) ? raw : raw.data || [];
      } catch (e) {
        console.error(e);
        this.formTemplates = [];
      }
    },

    openStepFormModal(idx) {
      const step = this.steps[idx];

      this.stepFormModal = {
        open: true,
        stepIdx: idx,
        form_required: !!step.form_required,
        mode: "existing",
        form_template_id: step.form_template_id || "",
        newTitle: "",
        questions: [],
      };
      this.stepFormError = "";
      this.applyFormTemplatePreview();
    },

    closeStepFormModal() {
      this.stepFormModal.open = false;
      this.stepFormError = "";
    },

    applyFormTemplatePreview() {
      const tpl = this.formTemplates.find(
        (t) => t.id === this.stepFormModal.form_template_id
      );
      this.stepFormModal.questions = tpl ? tpl.questions || [] : [];
    },

    async saveStepFormModal() {
      const { stepIdx, mode, form_required } = this.stepFormModal;
      this.stepFormError = "";

      if (mode === "existing") {
        if (!this.stepFormModal.form_template_id) {
          this.stepFormError = "Selecione um modelo de formulário.";
          return;
        }

        this.$set(this.steps[stepIdx], "form_template_id", this.stepFormModal.form_template_id);
        this.$set(this.steps[stepIdx], "form_required", !!form_required);
        this.closeStepFormModal();
        return;
      }

      if (!this.stepFormModal.newTitle.trim()) {
        this.stepFormError = "Informe o título do novo modelo.";
        return;
      }
      if (!this.stepFormModal.questions.length) {
        this.stepFormError = "Adicione ao menos uma pergunta ao formulário.";
        return;
      }

      this.savingStepForm = true;

      try {
        const apiBase = this.$store.state.api;
        const { data } = await this.$http.post(apiBase + "form-templates", {
          title: this.stepFormModal.newTitle,
          questions: this.stepFormModal.questions.map((q) => ({
            label: q.label,
            field_type: q.field_type,
            options: q.options || [],
            required: !!q.required,
          })),
        });

        await this.loadFormTemplates();

        this.$set(this.steps[stepIdx], "form_template_id", data.id);
        this.$set(this.steps[stepIdx], "form_required", !!form_required);
        this.closeStepFormModal();
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data ||
          "Erro ao criar o modelo de formulário.";
        this.stepFormError = typeof msg === "string" ? msg : JSON.stringify(msg);
      } finally {
        this.savingStepForm = false;
      }
    },

    removeStepForm() {
      const idx = this.stepFormModal.stepIdx;
      if (idx === null) return;
      this.$set(this.steps[idx], "form_template_id", "");
      this.$set(this.steps[idx], "form_required", false);
      this.closeStepFormModal();
    },

    async loadTemplate(id) {
      try {
        const apiBase = this.$store.state.api;
        const res = await this.$http.get(apiBase + "flow-templates/" + id);
        const tpl = res.data;

        this.form.title = tpl.title || "";
        this.form.tag = tpl.tag || "";
        this.form.description = tpl.description || "";

        if (tpl.transitions && tpl.transitions.length) {
          this.steps = tpl.transitions
            .slice()
            .sort((a, b) => Number(a.step_order) - Number(b.step_order))
            .map((s, i) => ({
              uid: `tpl_${s.id}_${i}`,
              name: s.name || "",
              department_id: s.department_id || "",
              requires_approval: !!s.requires_approval,
              approver_user_id: s.approver_user_id || "",
              form_template_id: s.form_template_id || "",
              form_required: !!s.form_required,
            }));
        }
      } catch (e) {
        console.error(e);
        this.error = "Não consegui carregar o modelo.";
      }
    },

    deptName(id) {
      if (!id) return "";
      const d = this.departments.find((x) => x.id == id);
      return d ? d.name : "";
    },

    numClass(idx) {
      if (idx === 0) return "n-blue";
      if (idx === 1) return "n-green";
      return "n-gray";
    },

    validate() {
      this.error = "";
      if (!this.form.title.trim()) {
        this.error = "Informe o título do modelo.";
        return false;
      }
      if (this.steps.length < 1) {
        this.error = "Adicione ao menos uma etapa.";
        return false;
      }
      return true;
    },

    async save() {
      if (!this.validate()) return;
      this.saving = true;
      this.error = "";
      try {
        const apiBase = this.$store.state.api;
        const payload = {
          title: this.form.title,
          tag: this.form.tag || null,
          description: this.form.description || null,
          steps: this.steps.map((s, i) => ({
            name: s.name,
            department_id: s.department_id || null,
            requires_approval: !!s.requires_approval,
            approver_user_id: s.requires_approval
              ? s.approver_user_id || null
              : null,
            form_template_id: s.form_template_id || null,
            form_required: !!s.form_required,
          })),
        };

        if (this.isEdit) {
          await this.$http.put(
            apiBase + "flow-templates/" + this.$route.params.id,
            payload
          );
        } else {
          await this.$http.post(apiBase + "flow-templates", payload);
        }

        this.$router.push("/Flow/templates");
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data ||
          "Erro ao salvar o modelo.";
        this.error = typeof msg === "string" ? msg : JSON.stringify(msg);
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$router.push("/Flow/templates");
    },

    openAddStepModal() {
      this.stepError = "";
      this.newStep = {
        name: "",
        department_id: "",
        requires_approval: false,
        approver_user_id: "",
      };
      this.showStepModal = true;
    },

    closeAddStepModal() {
      this.showStepModal = false;
      this.stepError = "";
      this.newStep = {
        name: "",
        department_id: "",
        requires_approval: false,
        approver_user_id: "",
      };
    },

    confirmAddStep() {
      this.stepError = "";
      const name = (this.newStep.name || "").trim();
      if (!name) {
        this.stepError = "Informe o nome da etapa.";
        return;
      }
      if (!this.newStep.department_id) {
        this.stepError = "Selecione a área.";
        return;
      }
      if (this.newStep.requires_approval && !this.newStep.approver_user_id) {
        this.stepError = "Selecione o usuário aprovador.";
        return;
      }

      this.steps.push({
        uid: `s_${Date.now()}_${Math.random().toString(16).slice(2)}`,
        name,
        department_id: this.newStep.department_id,
        requires_approval: !!this.newStep.requires_approval,
        approver_user_id: this.newStep.approver_user_id || "",
        form_template_id: "",
        form_required: false,
      });

      this.selectedStepIndex = this.steps.length - 1;
      this.closeAddStepModal();
    },

    removeStep(idx) {
      if (idx === 0 || this.steps.length <= 1) return;
      this.steps.splice(idx, 1);
      if (this.selectedStepIndex >= this.steps.length) {
        this.selectedStepIndex = this.steps.length - 1;
      }
    },

    moveStep(idx, dir) {
      const to = idx + dir;
      if (idx === 0 || to === 0 || to < 0 || to >= this.steps.length) return;
      const copy = [...this.steps];
      [copy[idx], copy[to]] = [copy[to], copy[idx]];
      this.steps = copy;
      if (this.selectedStepIndex === idx) this.selectedStepIndex = to;
      else if (this.selectedStepIndex === to) this.selectedStepIndex = idx;
    },
  },
};
</script>

<style scoped>
.bread {
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}
.sep2 {
  margin: 0 6px;
  color: #9ca3af;
}
.bread-link {
  cursor: pointer;
  color: #2563eb;
  text-decoration: underline;
}
.page-title {
  font-weight: 700;
  color: #111827;
}
.form-control {
  height: 38px;
}
.text-area {
  height: auto;
  min-height: 88px;
  resize: vertical;
  padding-top: 10px;
}
.section-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
}
.section-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 12px;
}
.lbl {
  font-weight: 800;
  color: #374151;
  margin-bottom: 6px;
}
.req {
  color: #dc2626;
}
.help-muted {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.steps-shell {
  border: 1px dashed #e5e7eb;
  background: #fafafa;
  border-radius: 10px;
  padding: 14px;
  position: relative;
}
.steps-line {
  position: absolute;
  left: 24px;
  right: 24px;
  top: 52px;
  height: 2px;
  background: #e5e7eb;
}
.steps {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 0;
}
.step {
  position: relative;
  display: flex;
  align-items: center;
}
.step-pill {
  min-width: 200px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-shadow: 0 1px 8px rgba(17, 24, 39, 0.04);
}
.step.active .step-pill {
  border-color: #93c5fd;
  box-shadow: 0 1px 10px rgba(59, 130, 246, 0.12);
}
.step-num {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 900;
  font-size: 12px;
  flex-shrink: 0;
}
.n-blue {
  background: #3b82f6;
}
.n-green {
  background: #22c55e;
}
.n-gray {
  background: #9ca3af;
}
.step-info {
  flex: 1;
  min-width: 0;
}
.step-name {
  font-weight: 900;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.step-dept {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.step-x {
  border: 0;
  background: transparent;
  font-size: 18px;
  font-weight: 900;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
}
.step-x:hover {
  color: #ef4444;
}
.arrow {
  margin: 0 8px;
  color: #9ca3af;
  font-weight: 900;
}
.step-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 8px;
}
.mini-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
}
.mini-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.new-step {
  min-width: 160px;
  height: 48px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #fff;
  font-weight: 900;
  color: #4b5563;
  cursor: pointer;
}
.new-step:hover {
  border-color: #93c5fd;
  color: #1d4ed8;
}

/* Modal */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 18px;
}
.modal-card {
  width: 520px;
  max-width: 95vw;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.18);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
}
.modal-title {
  font-weight: 900;
  color: #111827;
}
.modal-x {
  border: 0;
  background: transparent;
  font-size: 22px;
  font-weight: 900;
  color: #6b7280;
  cursor: pointer;
}
.modal-body {
  padding: 14px 16px;
}
.modal-foot {
  padding: 14px 16px;
  border-top: 1px solid #eef2f7;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.modal-card-lg {
  width: 640px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}

.step-form-badge {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 800;
  color: #1d4ed8;
  white-space: nowrap;
}

.step-form-btn {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #1d4ed8;
  border-radius: 8px;
  width: 26px;
  height: 26px;
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.step-form-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}

.mode-tabs {
  display: flex;
  gap: 8px;
}

.mode-tab {
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
}

.mode-tab.active {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.form-preview {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
}

.form-preview-title {
  font-size: 11px;
  font-weight: 800;
  color: #6b7280;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.form-preview-item {
  font-size: 13px;
  color: #374151;
  margin-top: 4px;
}
</style>
